import React, { forwardRef, useState, useRef } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../assets/styles/datepicker-custom.css';
import '../../assets/styles/datetimepicker-custom.css';
import { registerLocale } from 'react-datepicker';
import ptBR from 'date-fns/locale/pt-BR';
import { FaCalendarAlt, FaTimes } from 'react-icons/fa';

registerLocale('pt-BR', ptBR);

export const dateTimeHelpers = {
    stringToDateTime: (dateTimeString) => {
        if (!dateTimeString || !dateTimeString.includes('/')) {
            return null;
        }

        const parts = dateTimeString.split(' ');
        if (parts.length !== 2) return null;

        const [datePart, timePart] = parts;
        const [day, month, year] = datePart.split('/');
        const [hours, minutes] = timePart.split(':');

        if (!day || !month || !year || !hours || !minutes) {
            return null;
        }

        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hours), parseInt(minutes));
    },

    dateTimeToString: (date) => {
        if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
            return '';
        }
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    },

    createOnChangeHandler: (onChangeCallback) => {
        return (date) => {
            if (!date) {
                onChangeCallback('');
                return;
            }
            const formattedDateTime = dateTimeHelpers.dateTimeToString(date);
            onChangeCallback(formattedDateTime);
        };
    }
};

const applyDateTimeMask = (value) => {
    let cleaned = value.replace(/\D/g, '');

    if (cleaned.length > 12) {
        cleaned = cleaned.substring(0, 12);
    }

    let formatted = '';
    if (cleaned.length > 0) {
        formatted = cleaned.substring(0, 2);
        if (cleaned.length > 2) {
            formatted += '/' + cleaned.substring(2, 4);
        }
        if (cleaned.length > 4) {
            formatted += '/' + cleaned.substring(4, 8);
        }
        if (cleaned.length > 8) {
            formatted += ' ' + cleaned.substring(8, 10);
        }
        if (cleaned.length > 10) {
            formatted += ':' + cleaned.substring(10, 12);
        }
    }

    return formatted;
};

const parseInputToDateTime = (inputValue) => {
    if (!inputValue) return null;

    const dateTimeParts = inputValue.split(' ');
    const datePart = dateTimeParts[0];
    const timePart = dateTimeParts[1];

    const parts = datePart.split('/');
    if (parts.length !== 3) return null;

    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
    if (day < 1 || day > 31) return null;
    if (month < 1 || month > 12) return null;
    if (year < 1900 || year > 2100) return null;
    if (parts[2].length !== 4) return null;

    let hours = 0;
    let minutes = 0;

    if (timePart) {
        const timePartsSplit = timePart.split(':');
        if (timePartsSplit.length === 2) {
            hours = parseInt(timePartsSplit[0], 10);
            minutes = parseInt(timePartsSplit[1], 10);
            if (isNaN(hours) || isNaN(minutes)) return null;
            if (hours < 0 || hours > 23) return null;
            if (minutes < 0 || minutes > 59) return null;
        }
    }

    const date = new Date(year, month - 1, day, hours, minutes);

    if (date.getDate() !== day || date.getMonth() !== month - 1 || date.getFullYear() !== year) {
        return null;
    }

    return date;
};

const CustomInput = forwardRef(({
    value,
    placeholder,
    className,
    maxLength = 16,
    disabled,
    onInputChange,
    onCalendarClick,
    isClearable,
    onClear
}, ref) => {
    const [inputValue, setInputValue] = useState(value || '');

    React.useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    const handleChange = (e) => {
        if (disabled) return;
        const rawValue = e.target.value;
        const masked = applyDateTimeMask(rawValue);
        setInputValue(masked);

        if (onInputChange) {
            onInputChange(masked);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
        }
    };

    const handleClear = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setInputValue('');
        if (onClear) {
            onClear();
        }
    };

    return (
        <div className="input-group">
            <input
                className={className}
                placeholder={placeholder}
                value={inputValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                ref={ref}
                maxLength={maxLength}
                disabled={disabled}
                autoComplete="off"
            />
            {isClearable && inputValue && !disabled && (
                <button
                    type="button"
                    className="btn btn-clear"
                    onClick={handleClear}
                    title="Limpar"
                >
                    <FaTimes />
                </button>
            )}
            <button
                type="button"
                className="btn btn-calendar"
                onClick={onCalendarClick}
                title="Abrir calendário"
                disabled={disabled}
            >
                <FaCalendarAlt />
            </button>
        </div>
    );
});

const DateTimePicker = ({
    selected,
    onChange,
    placeholderText = "dd/mm/yyyy hh:mm",
    dateFormat = "dd/MM/yyyy HH:mm",
    className = "form-control",
    isClearable = true,
    showYearDropdown = true,
    showMonthDropdown = true,
    dropdownMode = "select",
    locale = "pt-BR",
    timeIntervals = 15,
    timeCaption = "Hora",
    showTimeSelect = true,
    disabled = false,
    popperModifiers = [
        {
            name: 'preventOverflow',
            options: {
                rootBoundary: 'viewport'
            }
        }
    ],
    popperProps = {
        strategy: 'fixed'
    },
    ...props
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const datePickerRef = useRef(null);

    const handleInputChange = (maskedValue) => {
        const parsedDate = parseInputToDateTime(maskedValue);
        if (parsedDate) {
            onChange(parsedDate);
        } else if (maskedValue === '') {
            onChange(null);
        }
    };

    const handleCalendarClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) {
            setIsOpen(!isOpen);
        }
    };

    const handleCalendarClose = () => {
        setIsOpen(false);
    };

    const handleDateChange = (date) => {
        onChange(date);
    };

    const handleClear = () => {
        onChange(null);
    };

    return (
        <ReactDatePicker
            ref={datePickerRef}
            selected={selected}
            onChange={handleDateChange}
            dateFormat={dateFormat}
            locale={locale}
            placeholderText={placeholderText}
            isClearable={false}
            showYearDropdown={showYearDropdown}
            showMonthDropdown={showMonthDropdown}
            dropdownMode={dropdownMode}
            showTimeSelect={showTimeSelect}
            timeIntervals={timeIntervals}
            timeCaption={timeCaption}
            timeFormat="HH:mm"
            disabled={disabled}
            open={isOpen}
            onClickOutside={handleCalendarClose}
            customInput={
                <CustomInput
                    className={className}
                    placeholder={placeholderText}
                    maxLength={16}
                    disabled={disabled}
                    onInputChange={handleInputChange}
                    onCalendarClick={handleCalendarClick}
                    isClearable={isClearable}
                    onClear={handleClear}
                />
            }
            popperModifiers={popperModifiers}
            popperProps={popperProps}
            {...props}
        />
    );
};

export default DateTimePicker;
